'use memo';

import { useContext, useState } from 'react';
import {
  AgxButton,
  AgxModal,
  AgxSpacer,
  AgxTypography,
  Breakpoints,
  Icon_AlertCircleOutline,
  Icon_SparkleForm,
  useWindowSize,
} from '@urbanx/agx.ui-components2';
import { FormConfigType } from '@urbanx/agx-ui-components';
import { isEqual } from 'lodash-es';
import { AuthContext } from '@urbanx/agx.auth.client';
import { cleanTwoLineAddress } from 'utils/formatAddress';
import { Tabs } from 'types/Tabs';
import { setAndShowErrorToast } from 'store/config';
import useStateFormLabels from '../../../../../form/FormLabels/useStateFormLabels';
import { completeCampaignStep } from 'Api/Campaigns/campaignsApi';
import { useFormLoader } from 'hooks/useFormLoader';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { getEnumValue } from 'helpers/enumHelper';
import {
  CampaignDetailModel,
  CampaignDetailModelStage,
  CampaignDetailModelState,
  FormType,
  InProgressFormModelFormType,
} from '@urbanx/agx.campaigns.client';

interface ResumeFormDetailsProps {
  campaign: CampaignDetailModel;
  currentTab: Tabs;
  mobile?: boolean;
}

const DISABLED_STAGES: CampaignDetailModelStage[] = [
  CampaignDetailModelStage.AgreementAgentSigning,
  CampaignDetailModelStage.AgreementVendorSigning,
];

const ResumeFormDetails = ({
  campaign,
  currentTab,
  mobile = false,
}: ResumeFormDetailsProps) => {
  const { inProgressForms, address, generatedDocuments, campaignId, stage } =
    campaign;

  const availableForms = useAppSelector(state => state.forms.availableForms);

  const dispatch = useAppDispatch();
  const { getAuthToken } = useContext(AuthContext);
  const { loadForm, loadFormWithConfig } = useFormLoader();

  const [showConfirmationModel, setShowConfirmationModel] = useState(false);
  const [disableButtons, setDisableButtons] = useState(() =>
    DISABLED_STAGES.includes(stage as CampaignDetailModelStage)
  );

  const screenSize = useWindowSize();
  const isMobile = screenSize === Breakpoints.Mobile;

  const formName = useStateFormLabels(
    campaign.state ?? CampaignDetailModelState.QLD,
    'formName'
  );

  const isHaveGeneratedDocs = (generatedDocuments?.length ?? 0) > 0;

  const validInProgressForms = inProgressForms?.filter(ipf => ipf != null);

  const inProgressForm =
    campaign.stage === CampaignDetailModelStage.AgreementSubmissionIncomplete
      ? validInProgressForms?.find(
          ipf => ipf.formType === InProgressFormModelFormType.ListingSubmission
        )
      : validInProgressForms?.find(
          ipf => ipf.formType === InProgressFormModelFormType.ListingGenerator
        );

  if (!inProgressForm) {
    return <>No in progress forms found</>;
  }

  if (!formName || !availableForms) return;

  const { formId } = inProgressForm;

  const [addressLineOne, addressLineTwo] = cleanTwoLineAddress(address);

  const resumeAgencyAgreement = async () => {
    try {
      setDisableButtons(true);

      await loadForm(campaignId ?? '', formId ?? '', FormConfigType.Campaign);
    } catch (err) {
      console.error(err);
      dispatch(setAndShowErrorToast('Failed to resume Agency Agreement'));
    }

    setDisableButtons(false);
  };

  const beginAgencyAgreementSubmission = async () => {
    const agencyAgreementSubmissionForm = availableForms.find(
      form =>
        form.type === FormType.ListingSubmission &&
        isEqual(form.state, campaign.state)
    );

    if (agencyAgreementSubmissionForm == null) {
      return;
    }

    try {
      setDisableButtons(true);

      const authToken = await getAuthToken();
      if (!authToken) return;

      const newForm = await completeCampaignStep(
        authToken,
        campaignId ?? '',
        true,
        inProgressForm?.formId
      );

      if (!newForm) {
        dispatch(
          setAndShowErrorToast('Failed to start Agency Agreement submission')
        );
        setDisableButtons(false);
        return;
      }

      await loadFormWithConfig(newForm);
    } catch (err) {
      dispatch(
        setAndShowErrorToast('Failed to start Agency Agreement submission')
      );
    }

    setDisableButtons(false);
  };

  const confirmationDialogBody = (
    <AgxSpacer
      direction="vertical"
      gap="spacing-xl"
      className="confirmationModalStyle"
    >
      <AgxTypography id="start-submission-warning" type="body" size="medium">
        You will not be able to edit the {formName} once you continue.
      </AgxTypography>
      <AgxTypography id="start-submission-question" type="body" size="medium">
        Continue to submission?
      </AgxTypography>
    </AgxSpacer>
  );

  const confirmationDialogActions = (
    <AgxSpacer
      direction="horizontal"
      gap="md"
      contentJustification="center"
      fill
      className="confirmationButtonContainerStyle"
    >
      <AgxButton
        id="startsubmission"
        size="large"
        variant="primary"
        text="Yes, start submission"
        onClick={beginAgencyAgreementSubmission}
        disabled={
          disableButtons ||
          ((availableForms == null || availableForms.length === 0) &&
            currentTab === Tabs.Archive)
        }
      />
      <AgxButton
        id="cancel"
        variant="hollow"
        size="large"
        disabled={disableButtons || currentTab === Tabs.Archive}
        text="No, go back"
        onClick={() => setShowConfirmationModel(false)}
      />
    </AgxSpacer>
  );

  return (
    <>
      <AgxTypography
        id="resume-form-address"
        type="heading"
        size="2x-large"
        style={{ textAlign: 'center' }}
      >
        {addressLineOne},
        <br />
        {addressLineTwo}
      </AgxTypography>
      {(campaign.stage ===
        CampaignDetailModelStage.AgreementSubmissionIncomplete ||
        !isHaveGeneratedDocs) && (
        <AgxSpacer direction="vertical" gap="xs" contentAlignment="center">
          <AgxTypography
            id="resume-form-more-fields"
            type="body"
            size="medium"
            style={{ textAlign: 'center' }}
          >
            There are more fields to fill,
          </AgxTypography>
          <AgxTypography
            id="resume-form-continue"
            type="body"
            size="medium"
            style={{ textAlign: 'center' }}
          >
            let's continue from where you left off
          </AgxTypography>
        </AgxSpacer>
      )}
      <Icon_SparkleForm
        width={isMobile ? 150 : 200}
        height={isMobile ? 150 : 200}
      />
      <AgxButton
        id="btn-resume"
        variant={mobile || isHaveGeneratedDocs ? 'primary' : 'hollow'}
        size="large"
        disabled={disableButtons || currentTab === Tabs.Archive}
        text={`Resume ${
          campaign.stage ===
          CampaignDetailModelStage.AgreementSubmissionIncomplete
            ? 'Submission'
            : 'Agreement'
        }`}
        onClick={resumeAgencyAgreement}
      />
      {!validInProgressForms?.some(
        ipf => ipf.formType === InProgressFormModelFormType.ListingSubmission
      ) &&
        !!campaign.agencyAgreementEnvelope &&
        isHaveGeneratedDocs && (
          <AgxButton
            id="btn-startSubmission"
            variant="hollow"
            size="large"
            disabled={disableButtons || currentTab === Tabs.Archive}
            text="Start Submission"
            onClick={() => setShowConfirmationModel(true)}
          />
        )}

      {showConfirmationModel && (
        <AgxModal
          id="resume-form-start-submission-modal"
          title="Starting a submission?"
          titleIcon={<Icon_AlertCircleOutline type="Warning" />}
          content={confirmationDialogBody}
          actionButtons={confirmationDialogActions}
          actionButtonContainerStyle="withoutDivider"
          type="single"
          variant="withoutDivider"
          setIsDialogOpen={setShowConfirmationModel}
        />
      )}
    </>
  );
};

export default ResumeFormDetails;
